// Compacts widgets up-and-to-the-left after a removal. react-grid-layout
// uses a vertical-only compactor at idle, so removing a widget pulls
// other widgets up but leaves any horizontal gap behind. This helper is
// a one-shot rearrange that fills both axes.
//
// Algorithm: process widgets in (y, x) order; for each, find the
// topmost-then-leftmost position that doesn't collide with already-
// placed widgets. The result is the unique layout where every widget
// sits at its most up-left valid position.

import type { WidgetInstance } from "./types";

function collides(a: WidgetInstance, b: WidgetInstance): boolean {
  if (a.id === b.id) return false;
  if (a.x + a.w <= b.x) return false;
  if (a.x >= b.x + b.w) return false;
  if (a.y + a.h <= b.y) return false;
  if (a.y >= b.y + b.h) return false;
  return true;
}

export function compactUpLeft(
  widgets: ReadonlyArray<WidgetInstance>,
  cols: number,
): WidgetInstance[] {
  const sorted = [...widgets].sort((a, b) => a.y - b.y || a.x - b.x);
  const placed: WidgetInstance[] = [];
  // Safety bound: a widget never needs a y past the sum of every widget's
  // height (worst case is a single column stack).
  const yBound =
    sorted.reduce((acc, w) => acc + w.h, 0) + sorted.length;

  for (const item of sorted) {
    let foundX = item.x;
    let foundY = item.y;
    let found = false;
    for (let y = 0; y <= yBound && !found; y += 1) {
      for (let x = 0; x <= cols - item.w; x += 1) {
        const candidate: WidgetInstance = { ...item, x, y };
        if (!placed.some((p) => collides(p, candidate))) {
          foundX = x;
          foundY = y;
          found = true;
          break;
        }
      }
    }
    placed.push({ ...item, x: foundX, y: foundY });
  }
  return placed;
}
